import { Component, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from '@services/auth.service';
import { DialogService } from '@services/dialog.service';
import { LangService } from '@services/lang.service';
import { UserService } from '@services/user.service';
import { ISsoReply, SsoService } from '@services/sso.service';
import { EmployerService } from '@services/employer.service';
import { FORM_COMP_MODES, ROLES } from '@services/constants.service';

import { User } from '@models/user';

import { environment } from '@envs/environment';
import { Party } from '@models/party';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: false
})
export class SettingsComponent implements OnInit {

  public currentUser: User | null = null;

  public showPasswordForm: boolean = false;
  public showNameForm: boolean = false;
  public showEmailForm: boolean = false;
  public selectedLanguage: string = '';
  public ssoProfileEditLoading: boolean = false;
  public ssoEmailChangeLoading: boolean = false;
  public ssoPasswordLoading: boolean = false;

  public loadingSso: boolean = true;
  public ssoUserExists: boolean = false;
  public linkingSso: boolean = false;
  public registeringSso: boolean = false;

  private currentLanguage: string;
  public partyId: string | null = null;

  private adminModal: NgbModalRef | null = null;
  public adminError: string = '';
  public userProps = { employerPartyId: "", employeeRoles: [ROLES.ADMIN] };

  public environment = environment;

  // necessary to reference it in the html template
  public readonly FORM_COMP_MODES = FORM_COMP_MODES;

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private langService: LangService,
    private dialogService: DialogService,
    private modalService: NgbModal,
    private userService: UserService,
    private ssoService: SsoService,
    private employerService: EmployerService,
  ) {
    this.currentLanguage = this.translateService.currentLang() as string;
  }

  ngOnInit(): void {
    this.authService.getCurrentUser$().subscribe((currentUser: User | null) => {
      this.loadingSso = true;
      this.ssoUserExists = false;
      if (currentUser) {
        this.currentUser = currentUser;

        this.langService.getLangAttribute(currentUser.partyId!).then((lang: string) => {
          this.selectedLanguage = lang;
        });

        this.ssoService.checkEmail(this.currentUser?.email!).then((reply: ISsoReply) => {
          this.loadingSso = false;
          if (reply.externalAuthId) {
            this.ssoUserExists = true;
          }
        });
      }
    });

    this.employerService.getCurrentEmployerParty$().subscribe((party: Party | null) => {
      this.userProps.employerPartyId = party?.partyId || '';
    });
  }

  public onLangChange(selectedLanguage: string) {
    this.currentLanguage = selectedLanguage;
    if (this.currentUser) {
      this.partyId = this.userService.getPartyId(this.currentUser);
      this.langService.setLangAttribute(this.partyId || '', this.currentLanguage || '');
      this.langService.setLanguage(this.currentLanguage);

    } else {
      console.error('either currentUser or currentLanguage is null.');
    }
  }

  public exitNameForm(event: boolean) {
    if (event) {
      this.showNameForm = false;
      this.dialogService.alert(
        null,
        this.translateService.instant("Name erfolgreich geändert."),
        "success"
      );
    }
  }

  public exitEmailForm(event: boolean) {
    if (event) {
      this.showEmailForm = false;
      this.dialogService.alert(
        null,
        this.translateService.instant("Email erfolgreich geändert."),
        "success"
      );
    } else {
      this.dialogService.alert(
        null,
        this.translateService.instant("Email konnte nicht geändert werden."),
        "danger"
      );
    }
  }

  public exitPasswordForm(event: boolean) {
    if (event) {
      this.showPasswordForm = false;
    } else {
      this.dialogService.alert("", this.translateService.instant("Dialog.SettingsComponent.msg_errorWhileSavingPassword"));
    }
  }

  public openAdminModal(modal: any) {
    this.adminModal = this.modalService.open(modal);

    this.adminModal.result.then(() => {
      this.adminError = '';
    });
  }

  public exitUserForm(event: boolean) {
    this.adminModal?.close();
    if (event == true) {
      this.dialogService.alert(
        null,
        this.translateService.instant("Dialog.SettingsComponent.msg_adminCreated", { email: "" })
      );
    }
  }

  public openEditSsoName() {
    this.ssoProfileEditLoading = true;
    this.ssoService.redirectToProfileEdit();
  }

  public openEditSsoEmail() {
    this.ssoEmailChangeLoading = true;
    this.ssoService.redirectToChangeEmail();
  }

  public openResetSsoPassword() {
    this.ssoPasswordLoading = true;
    this.ssoService.redirectToPasswordReset();
  }

  public redirectToRegister() {
    this.registeringSso = true;

    this.authService.getCurrentUser().then((user) => {
      this.ssoService.redirectToRegister(user!, 'settings_link');
    }).catch((err) => {
      this.dialogService.alert('Error', this.translateService.instant('Common.text_genericError'));
    });
  }

  public redirectToSignInAndLink(): void {
    this.linkingSso = true;

    this.authService.getCurrentUser().then((user) => {
      this.ssoService.redirectToLogin('settings_link');
    }).catch((err) => {
      this.dialogService.alert('Error', this.translateService.instant('Common.text_genericError'));
    });
  }

}
